import { atom } from 'jotai';
import { PrimitiveAtom } from 'jotai/vanilla/atom';
import { SetStateAction } from 'react';

import { isSetStateFunction } from './is-set-state-function';
import { PersistStateDelegate } from './persist-state.delegate';

export type PersistStateAtom<T extends object, K extends Extract<keyof T, string>> = PrimitiveAtom<T[K]>;
export interface PersistStateOptions<T extends object, K extends Extract<keyof T, string>> {
  defaultValue: T[K];
  delegate: PersistStateDelegate<T>;
}

export const createPersistStateAtom = <T extends object, K extends Extract<keyof T, string>>(
  key: K,
  options: PersistStateOptions<T, K>,
): PersistStateAtom<T, K> => {
  const { delegate, defaultValue } = options;

  const internalAtom = atom<{ value: T[K] }>({ value: defaultValue });
  internalAtom.onMount = (set) => {
    const result = delegate.getItem(key);
    if (result instanceof Promise) {
      result.then((value) => set({ value: value ?? defaultValue }));
    } else {
      set({ value: result ?? defaultValue });
    }
    return delegate.subscribe(
      key,
      (value) => {
        set({ value });
      },
      defaultValue,
    );
  };
  const stateAtom = atom(
    (get) => get(internalAtom)?.value,
    (get, set, action: SetStateAction<T[K]>) => {
      const prev = get(stateAtom);
      const nextState = isSetStateFunction(action) ? action(prev) : action;
      delegate.setItem(key, nextState);
      set(internalAtom, { value: nextState ?? defaultValue });
    },
  );

  return stateAtom;
};
